import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { KeyRound, Trash2, PlugZap, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

import { API } from '../../lib/appConfig';

// Aba de Configurações — gerenciamento seguro da chave da API de análises aprofundadas.
// A chave nunca é exibida em claro: o backend retorna apenas status + últimos 4 caracteres.
export const SettingsTab = ({ isDarkMode }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }

  const cardClass = `rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`;
  const labelClass = `text-sm font-medium ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`;
  const mutedClass = `text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`;
  const inputClass = `w-full rounded-md border px-3 py-2 text-sm outline-none transition-colors focus:border-demoraes-green ${
    isDarkMode ? 'bg-zinc-950 border-zinc-700 text-zinc-100 placeholder-zinc-600' : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400'
  }`;

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/settings/ai`);
      setStatus(r.data);
    } catch (e) {
      console.error(e);
      setFeedback({ type: 'error', message: 'Não foi possível carregar o status da configuração.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const notify = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 6000);
  };

  const saveKey = async () => {
    if (!apiKey.trim() || saving) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/ai`, { api_key: apiKey.trim() });
      setStatus(r.data);
      setApiKey('');
      setShowKey(false);
      notify('success', 'Chave salva com sucesso. Use "Testar conexão" para validar.');
    } catch (e) {
      notify('error', e.response?.data?.detail || 'Erro ao salvar a chave.');
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    if (deleting) return;
    if (!window.confirm('Remover a chave da API? As análises aprofundadas deixarão de funcionar até uma nova chave ser configurada.')) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/admin/settings/ai`);
      setStatus(r.data);
      notify('success', 'Chave removida.');
    } catch (e) {
      notify('error', e.response?.data?.detail || 'Erro ao remover a chave.');
    } finally {
      setDeleting(false);
    }
  };

  const testConnection = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/settings/ai/test`);
      if (r.data?.ok) {
        notify('success', 'Conexão validada com sucesso. Análises aprofundadas operacionais.');
      } else {
        notify('error', r.data?.detail || 'Falha na validação da chave.');
      }
    } catch (e) {
      notify('error', e.response?.data?.detail || 'Falha ao testar a conexão.');
    } finally {
      setTesting(false);
    }
  };

  const configured = status?.configured;
  const fromEnv = status?.source === 'env';

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-tab">
      {/* Status */}
      <div className={cardClass}>
        <div className="flex items-center gap-3 mb-4">
          <KeyRound size={18} className="text-demoraes-green flex-shrink-0" />
          <h2 className={labelClass} style={{ fontFamily: 'Manrope, sans-serif' }}>
            Análises Aprofundadas — Chave da API
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin text-demoraes-green" />
            <span className={mutedClass}>Carregando status...</span>
          </div>
        ) : (
          <div className="flex items-center gap-2" data-testid="ai-key-status">
            {configured ? (
              <CheckCircle2 size={16} className="text-demoraes-green flex-shrink-0" />
            ) : (
              <XCircle size={16} className="text-red-500 flex-shrink-0" />
            )}
            <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
              {configured
                ? `Chave configurada (•••• ${status?.hint || ''})${fromEnv ? ' — via variável de ambiente' : ''}`
                : 'Nenhuma chave configurada. As análises aprofundadas estão indisponíveis.'}
            </span>
          </div>
        )}

        {fromEnv && (
          <p className={`${mutedClass} mt-2`}>
            A chave está definida no arquivo .env do servidor (ANTHROPIC_API_KEY) e prevalece
            sobre a configuração feita por aqui. Para gerenciar pela interface, remova-a do .env.
          </p>
        )}
      </div>

      {/* Save / update */}
      {!fromEnv && (
        <div className={cardClass}>
          <label className={labelClass} htmlFor="ai-api-key-input">
            {configured ? 'Substituir chave' : 'Configurar chave'}
          </label>
          <p className={`${mutedClass} mt-1 mb-3`}>
            A chave é armazenada criptografada no banco de dados e nunca é exibida em claro.
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                id="ai-api-key-input"
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveKey(); }}
                placeholder="sk-ant-..."
                autoComplete="off"
                spellCheck={false}
                className={inputClass}
                data-testid="ai-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`}
                title={showKey ? 'Ocultar' : 'Mostrar'}
                tabIndex={-1}
              >
                {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <button
              onClick={saveKey}
              disabled={!apiKey.trim() || saving}
              className="px-4 py-2 rounded-md text-sm font-medium bg-demoraes-green text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
              data-testid="ai-key-save"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Salvar
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      {configured && (
        <div className={cardClass}>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={testConnection}
              disabled={testing}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors flex items-center gap-2 disabled:opacity-40 ${
                isDarkMode ? 'border-zinc-700 text-zinc-200 hover:bg-zinc-800' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'
              }`}
              data-testid="ai-key-test"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />}
              Testar conexão
            </button>
            {!fromEnv && (
              <button
                onClick={removeKey}
                disabled={deleting}
                className={`px-4 py-2 rounded-md text-sm font-medium border border-red-300 text-red-500 transition-colors flex items-center gap-2 disabled:opacity-40 ${
                  isDarkMode ? 'border-red-900 hover:bg-red-950/30' : 'hover:bg-red-50'
                }`}
                data-testid="ai-key-delete"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Remover chave
              </button>
            )}
          </div>
        </div>
      )}

      {/* Feedback */}
      {feedback && (
        <div
          className={`rounded-md border px-4 py-3 text-sm flex items-center gap-2 ${
            feedback.type === 'success'
              ? (isDarkMode ? 'border-green-900 bg-green-950/30 text-green-400' : 'border-green-200 bg-green-50 text-green-700')
              : (isDarkMode ? 'border-red-900 bg-red-950/30 text-red-400' : 'border-red-200 bg-red-50 text-red-700')
          }`}
          data-testid="settings-feedback"
        >
          {feedback.type === 'success' ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
          {feedback.message}
        </div>
      )}
    </div>
  );
};
